import { StreamMessageType } from '@/repositories/dashboard-repository'
import { Issue } from '@/types'

export enum IDTypes {
    GHANA_CARD = 'GhanaCard',
    PASSPORT = 'Passport',
    VOTERS_ID = 'VotersIdentificationCard',
    DRIVERS_LICENSE = 'DriversLicense',
    NHIS = 'NHIS',
}

export enum ServiceCategories {
    Standard = 'standard',
    Express = 'express',
    Premium = 'premium',
}

export enum ServiceTypes {
    NewService = 'NewServiceRequest',
    SeparateMeter = 'SeparateMeterRequest',
    AdditionalLoad = 'AdditionalLoadRequest',
    TemporarySupply = 'TemporarySupplyRequest',
}

export const REQUEST_TYPE_OPTIONS: Record<
    ServiceTypes,
    { label: string; description: string; value: ServiceTypes; note?: string }
> = {
    [ServiceTypes.NewService]: {
        label: 'New Service',
        description: 'Facilities that have never had electricity, that is no service point',
        value: ServiceTypes.NewService,
    },
    [ServiceTypes.SeparateMeter]: {
        label: 'Separate Meter',
        description: 'Existing service points with  monthly consumption greater than 300KWh',
        value: ServiceTypes.SeparateMeter,
    },
    [ServiceTypes.AdditionalLoad]: {
        label: 'Additional Load',
        description: 'Increasing the load on existing service point to 400V',
        value: ServiceTypes.AdditionalLoad,
    },
    [ServiceTypes.TemporarySupply]: {
        label: 'Temporary Supply',
        description: 'Short-term electricity supply to a temporal service point',
        value: ServiceTypes.TemporarySupply,
    },
}

export enum TypeOfService {
    singlePhase = 'single phase',
    threePhase = 'three phase',
}

export enum ConnectionPhase {
    SINGLE_PHASE = '1-Phase',
    THREE_PHASE = '3-Phase',
}

export enum ServiceRequestTypes {
    CHANGE_OF_ACCOUNT_DETAILS = 'ChangeOfAccountDetails',
    CUSTOMER_STATEMENT = 'CustomerStatement',
    TEMPORARY_DISCONNECTION = 'TemporaryDisconnection',
    METER_TRANSFER = 'MeterTransfer',
    FAULTY_METER = 'FaultyMeter',
    METER_CARD_REPLACEMENT = 'MeterCardReplacement',
}

export enum ReportIssueTypes {
    REPORT_FAULTY_METER = 'faulty meter',
    REPORT_ISSUE_WITH_POSTPAID_BILL = 'issue with Postpaid Bill',
    REPORT_ILLEGAL_CONNECTION = 'illegal connection',
    STOLEN_METER = 'stolen meter',
    STAFF_MISCONDUCT = 'staff-misconduct',
    DELAYED_INSTALLATION = 'delayed installation',
    PREPAID_METER_NOT_FOUND = 'prepaid-meter-not-found',
    METER_NOT_FOUND = 'meter not found',
    METER_CARD_REPLACEMENT = 'meter card replacement',
    METER_REPLACEMENT = 'meter replacement',
}

export enum ServiceRequestComplaintTypes {
    CHANGE_OF_ACCOUNT_DETAILS = 'change of account details',
    METER_TRANSFER = 'meter transfer',
    TEMPORARY_DISCONNECTION = 'temporary disconnection',
    STATEMENT_REQUEST = 'customer statement',
    NEW_SERVICE_REQUEST = 'service request',
    RECONNECTION_REQUEST = 'reconnection request',
    DELAYED_INSTALLATION = 'delayed installation',
    METER_CARD_REPLACEMENT = 'meter card replacement',
    METER_REPLACEMENT = 'meter replacement',
}

export enum NewServiceConnectionProgressActionType {
    SHOW_ADD_TO_METERS_MODAL = 'SHOW_ADD_TO_METERS_MODAL',
    SHOW_DELAYED_INSTALLATION_MODAL = 'SHOW_DELAYED_INSTALLATION_MODAL',
}

/**
 * A constant object that maps report issue types to their corresponding issue details or `false`.
 * Each issue contains a title, description, and issue type, providing information about
 * various reportable issues related to electricity services.
 *
 * @constant
 * @type {Record<string | ReportIssueTypes, Issue | false>}
 *
 * @property {string} title - The title of the issue.
 * @property {string} description - A brief description of the issue.
 * @property {string} issueType - The type of the issue, corresponding to a key in `ReportIssueTypes`.
 *
 * Example issues include:
 * - Issues with postpaid bills
 * - Faulty meters
 * - Meter card replacements
 * - Illegal connections
 * - Stolen meters
 *
 * Note: Some issues are commented out and not included in the current implementation.
 */
export const ReportIssues: Record<string, Issue> = {
    [ReportIssueTypes.REPORT_ISSUE_WITH_POSTPAID_BILL]: {
        title: 'Issue with Postpaid Bill',
        description: 'Postpaid bills are generated from meter readings, which may sometimes be wrong.',
        issueType: ReportIssueTypes.REPORT_ISSUE_WITH_POSTPAID_BILL,
    },
    [ReportIssueTypes.REPORT_FAULTY_METER]: {
        title: 'Faulty Meter',
        description: 'Report any issues relating to your meter.',
        issueType: ReportIssueTypes.REPORT_FAULTY_METER,
    },
    [ReportIssueTypes.METER_CARD_REPLACEMENT]: {
        issueType: ReportIssueTypes.METER_CARD_REPLACEMENT,
        title: 'Meter Card Replacement',
        description: 'Report issue relating to your prepaid card.',
    },
    [ReportIssueTypes.REPORT_ILLEGAL_CONNECTION]: {
        title: 'Illegal Connection',
        description:
            'Electricity is not free. When bad customers use for free, it makes it more expensive for good customers.',
        issueType: ReportIssueTypes.REPORT_ILLEGAL_CONNECTION,
    },
    [ReportIssueTypes.STOLEN_METER]: {
        issueType: ReportIssueTypes.STOLEN_METER,
        title: 'Stolen Meter',
        description: 'Report your meter that has been stolen',
    },
    // {
    //     title: 'Report Fault / Outage',
    //     description: 'Report any meter or transmission fault, billing or service issue',
    //     src: '/images/svg/outage.svg',
    // },
    // {
    //     title: 'Report Disconnection / New Service',
    //     description: 'Get help to resolve any disconnection issue or request for a new meter service',
    //     src: '/images/svg/disconnect.svg',
    // },
    // {
    //     title: 'General Enquiry',
    //     description: 'We’re here for you. Ask us about anything',
    //     src: '/images/svg/enquiry.svg',
    //     I,
    // },
    // {
    //     title: 'Staff Misconduct',
    //     description: 'Bad behaviour harms all of us and degrades the service quality. Report any suspicious persons',
    //     issueType: ReportIssueTypes.STAFF_MISCONDUCT,
    // },
}

export const StreamTypeImagesMap = {
    [StreamMessageType.CONNECTION_FEE_GENERATED]: '/images/icons/connection-fee-generated.svg',
    [StreamMessageType.CONNECTION_FEE_PAID]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.APPLICATION_FEE_GENERATED]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.APPLICATION_FEE_PAID]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.INSPECTION_INITIATED]: '/images/icons/connection-fee-generated.svg',
    [StreamMessageType.INSPECTION_SUCCESSFUL]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.CONTRACT_SIGNED]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.CONTRACT_GENERATED]: '/images/icons/connection-fee-generated.svg',
    [StreamMessageType.INSTALLATION_INITIATED]: '/images/icons/connection-fee-generated.svg',
    [StreamMessageType.INSTALLATION_SUCCESSFUL]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.INSTALLATION_FAILED]: '/images/icons/inspection-failed.svg',
    [StreamMessageType.SERVICE_REQUEST_COMPLETED]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.TARIFF_CLASS_UPDATED]: '/images/icons/application-fee-paid.svg',
    [StreamMessageType.NEW_METER_INSTALLED]: '/images/icons/new-meter-installed.svg',
    [StreamMessageType.METER_REPLACED]: '/images/icons/meter-replaced.svg',
    [StreamMessageType.REQUEST_ACCOUNT_DETAILS_CHANGE_REJECTED]: '/images/icons/details-change-rejected.svg',
    [StreamMessageType.REQUEST_ACCOUNT_DETAILS_CHANGE_APPROVED]: '/images/icons/details-change-approved.svg',
    [StreamMessageType.ACCOUNT_DETAILS_UPDATED]: '/images/icons/customer-details-updated.svg',
    [StreamMessageType.NEW_METER_READING]: '/images/icons/meter-replaced.svg',
    [StreamMessageType.METER_READING_EXCEPTION]: '/images/icons/new-meter-installed.svg',
    [StreamMessageType.METER_READING_ANOMALY]: '/images/icons/meter-reading-anomaly.svg',
}

export enum ServiceRequestTimelineStages {
    REQUESTED = 'Service Requested',
    ASSIGNED = 'Assigned for Assistance',
    SCHEDULED = 'Scheduled Visit for Inspection',
    PAYMENT = 'Make Payment',
    CARD_ISSUED = 'Card Issued',
    CARD_PICKED_UP = 'Card Picked Up',
    COMPLETED = 'Completed',
}

export enum CustomerStatementStatus {
    /**
     * Statement was generated successfully
     */
    Completed = 'completed',

    /**
     * Pending.
     */
    Pending = 'pending',

    /**
     * Statement pdf generation has been initiated
     */
    Processing = 'processing',

    /**
     * Customer report will be failed if the report was not generated after n retries,
     * transaction status will also be marked as failed
     */
    Failed = 'failed',

    /**
     * Automated refund initiated
     */
    RefundPending = 'refund pending',

    /**
     * Customer was refunded successfully
     */
    Refunded = 'refunded',

    /**
     * Refund was not completed after 24 hours.
     * Use this to show the report refund delay button on the notice
     */
    RefundFailed = 'refund failed',

    /**
     * Refund delay has been reported by the user.
     * Use this to hide the report refund delay button on notice
     */
    DelayedRefund = 'delayed refund',
}

export enum MeterBalanceStatus {
    DEBT = 'debt',
    CREDIT = 'credit',
    ZERO_BALANCE = 'zeroBalance',
}

export enum MeterReplacementIssueType {
    BALANCE_ISSUE = 'BalanceIssue',
    METER_REPLACEMENT = 'MeterReplacement',
}

export enum SERVICE_REQUEST_STATUS {
    INSPECTION_INITIATED = 'Inspection Initiated',
    INSPECTION_SUCCESSFUL = 'Inspection successful',
    CONTRACT_SIGNED = 'Contract Signed',
    CONTRACT_GENERATED = 'Contract Generated',
    INSTALLATION_INITIATED = 'Installation Initiated',
    INSTALLATION_SUCCESSFUL = 'Installation Successful',
    INSTALLATION_FAILED = 'Installation Failed',
}
